import LumaAccessiblePanel from '../../../components/admin/CoworkFormPanel';
import Loading from '../../../components/Loading';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Camera, Trash2, CheckCircle2, Shield, Copy, Check } from 'lucide-react';
import {
    createCorrespondencia,
    CorrespondenciaCreate,
    Correspondencia,
} from '../../../services/correspondencia';
import { fetchCitaCorrespondenciaPorCita } from '../../../services/cita-correspondencia';

interface IniciarCorrespondenciaModalProps {
    isOpen: boolean;
    onClose: () => void;
    citaId: number;
    citaCodigo: string;
}

const IniciarCorrespondenciaModal: React.FC<IniciarCorrespondenciaModalProps> = ({
    isOpen,
    onClose,
    citaId,
    citaCodigo
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingConfig, setIsLoadingConfig] = useState(false);
    const [configCorrespondenciaId, setConfigCorrespondenciaId] = useState<number | null>(null);
    const [imagenFile, setImagenFile] = useState<File | null>(null);
    const [imagenPreview, setImagenPreview] = useState<string | null>(null);
    const [correspondenciaCreada, setCorrespondenciaCreada] = useState<Correspondencia | null>(null);
    const [copiado, setCopiado] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [formData, setFormData] = useState<Omit<CorrespondenciaCreate, 'cita_correspondencia' | 'imagen'>>({
        estado: 'recibido',
        descripcion: '',
        remitente_nombre: '',
        remitente_apellido: '',
        remitente_telefono: '',
        remitente_ci_nit: ''
    });

    useEffect(() => {
        const loadConfig = async () => {
            if (isOpen && citaId) {
                setIsLoadingConfig(true);
                setCorrespondenciaCreada(null);
                setImagenFile(null);
                setImagenPreview(null);
                try {
                    const config = await fetchCitaCorrespondenciaPorCita(citaId);
                    if (config && config.habilitado) {
                        setConfigCorrespondenciaId(config.id);
                    } else {
                        alert('Esta cita no tiene correspondencia habilitada o configurada');
                        onClose();
                    }
                } catch (error) {
                    /* Browser diagnostics omitted. */
                    alert('Error al cargar configuración de correspondencia');
                    onClose();
                } finally {
                    setIsLoadingConfig(false);
                }
            }
        };

        loadConfig();
    }, [isOpen, citaId, onClose]);

    useEffect(() => {
        if (isOpen) {
            setFormData({
                estado: 'recibido',
                descripcion: '',
                remitente_nombre: '',
                remitente_apellido: '',
                remitente_telefono: '',
                remitente_ci_nit: ''
            });
            setImagenFile(null);
            setImagenPreview(null);
            setCorrespondenciaCreada(null);
        }
    }, [isOpen]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                alert('La imagen no debe superar los 10MB');
                return;
            }
            setImagenFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagenPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        setImagenFile(null);
        setImagenPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleCopiarPin = () => {
        if (correspondenciaCreada?.pin_seguridad) {
            navigator.clipboard.writeText(correspondenciaCreada.pin_seguridad);
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!configCorrespondenciaId) {
            alert('No se encontró configuración de correspondencia para esta cita');
            return;
        }

        setIsSubmitting(true);
        try {
            const correspondenciaData: CorrespondenciaCreate = {
                cita_correspondencia: configCorrespondenciaId,
                ...formData,
                imagen: imagenFile,
                receptor_nombre: '',
                receptor_apellido: '',
                receptor_telefono: '',
                receptor_ci_nit: ''
            };

            const nuevaCorrespondencia = await createCorrespondencia(correspondenciaData);
            setCorrespondenciaCreada(nuevaCorrespondencia);
        } catch (error: any) {
            /* Browser diagnostics omitted. */

            if (error.response) {
                const errorData = error.response.data;
                let errorMessage = 'Error al crear correspondencia:\n';

                for (const [field, messages] of Object.entries(errorData)) {
                    errorMessage += `- ${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}\n`;
                }

                alert(errorMessage);
            } else if (error.message) {
                alert(`Error: ${error.message}`);
            } else {
                alert('Error al crear correspondencia. Verifique los datos e intente nuevamente.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    if (isLoadingConfig) {
        return (
            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl p-8">
                    <Loading compact text="Cargando configuración de correspondencia…"/>
                </div>
            </LumaAccessibleDialog>
        );
    }

    if (!configCorrespondenciaId) {
        return (
            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <LumaAccessiblePanel data-luma-dialog="true" className="bg-white rounded-xl shadow-2xl max-w-md w-full">
                    <div className="p-6">
                        <div className="text-center">
                            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                                <X className="h-6 w-6 text-red-600" />
                            </div>
                            <LumaAccessibleTitle className="text-xl font-bold text-gray-800 mb-2">Configuración no encontrada</LumaAccessibleTitle>
                            <p className="text-gray-600 mb-6">
                                Esta cita no tiene correspondencia habilitada o configurada.
                            </p>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#3564db] to-[#92b1ed] hover:shadow-md rounded-lg transition-all"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        );
    }

    // Pantalla de éxito tras registrar la correspondencia
    if (correspondenciaCreada) {
        return (
            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <LumaAccessiblePanel data-luma-dialog="true" className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 transform transition-all animate-fade-in">
                    <div className="text-center space-y-6">
                        <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center text-green-600 shadow-inner">
                            <CheckCircle2 className="w-10 h-10" />
                        </div>

                        <div>
                            <h3 className="text-2xl font-bold text-gray-800">¡Correspondencia Registrada!</h3>
                            <p className="text-sm text-gray-500 mt-1">Se ha generado el código y las credenciales de seguridad</p>
                        </div>

                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-5 text-left space-y-4 shadow-sm">
                            <div className="flex justify-between items-center pb-3 border-b border-blue-100">
                                <div>
                                    <span className="text-xs font-medium text-blue-600 uppercase tracking-wider">Código de Seguimiento</span>
                                    <p className="text-lg font-bold text-gray-900 mt-0.5">{correspondenciaCreada.codigo || `#${correspondenciaCreada.id}`}</p>
                                </div>
                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                                    {correspondenciaCreada.estado}
                                </span>
                            </div>

                            {correspondenciaCreada.pin_seguridad && (
                                <div className="bg-white p-3.5 rounded-lg border border-blue-200 flex items-center justify-between shadow-sm">
                                    <div className="flex items-center gap-2.5">
                                        <Shield className="w-5 h-5 text-[#3564db]" />
                                        <div>
                                            <span className="text-xs text-gray-500 font-medium">PIN de Seguridad para Retiro</span>
                                            <p className="text-xl font-mono font-extrabold tracking-widest text-[#3564db]">
                                                {correspondenciaCreada.pin_seguridad}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleCopiarPin}
                                        className="p-2 text-gray-500 hover:text-[#3564db] hover:bg-blue-50 rounded-lg transition-colors"
                                        title="Copiar PIN"
                                    >
                                        {copiado ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                </div>
                            )}

                            {imagenPreview && (
                                <div className="pt-2">
                                    <span className="text-xs text-gray-500 font-medium block mb-1.5">Respaldo Fotográfico Registrado:</span>
                                    <img
                                        src={imagenPreview}
                                        alt="Respaldo"
                                        className="w-full h-32 object-cover rounded-lg border border-gray-200 shadow-sm"
                                    />
                                </div>
                            )}

                            <p className="text-xs text-gray-500 leading-relaxed italic">
                                * El cliente también podrá ver este código, su fotografía de respaldo y su PIN/QR de retiro desde su panel personal.
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full py-3 px-4 text-sm font-semibold text-white bg-gradient-to-r from-[#3564db] to-[#92b1ed] hover:shadow-lg rounded-xl transition-all duration-200"
                        >
                            Listo / Cerrar
                        </button>
                    </div>
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        );
    }

    return (
        <>
            <style>{`
                .correspondencia-scroll::-webkit-scrollbar {
                    width: 6px;
                }
                .correspondencia-scroll::-webkit-scrollbar-track {
                    background: transparent;
                }
                .correspondencia-scroll::-webkit-scrollbar-thumb {
                    background-color: rgba(0, 127, 200, 0.3);
                    border-radius: 20px;
                }
                .correspondencia-scroll::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(0, 127, 200, 0.5);
                }
                .correspondencia-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(0, 127, 200, 0.3) transparent;
                }
            `}</style>

            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <LumaAccessiblePanel data-luma-dialog="true" className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto correspondencia-scroll">
                    <div className="p-8">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">Iniciar Correspondencia</h2>
                                <p className="text-gray-600 mt-2">
                                    Crea una nueva correspondencia para la cita: <span className="font-semibold text-[#3564db] ml-1">{citaCodigo}</span>
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="h-5 w-5 text-gray-500" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-8"><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                            {/* Datos del Remitente */}
                            <div className="space-y-6">
                                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-r from-[#3564db]/10 to-[#92b1ed]/10 rounded-lg flex items-center justify-center">
                                        <div className="w-3 h-3 bg-[#3564db] rounded-full"></div>
                                    </div>
                                    Datos del Remitente
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Nombre *
                                        </label>
                                        <input
                                            type="text"
                                            name="remitente_nombre"
                                            value={formData.remitente_nombre}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3564db]/30 focus:border-[#3564db] transition-all"
                                            placeholder="Ej: Juan"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Apellido *
                                        </label>
                                        <input
                                            type="text"
                                            name="remitente_apellido"
                                            value={formData.remitente_apellido}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3564db]/30 focus:border-[#3564db] transition-all"
                                            placeholder="Ej: Pérez"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">
                                            Teléfono *
                                        </label>
                                        <input
                                            type="tel"
                                            name="remitente_telefono"
                                            value={formData.remitente_telefono}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3564db]/30 focus:border-[#3564db] transition-all"
                                            placeholder="Ej: 71234567"
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-gray-700">
                                            CI/NIT *
                                        </label>
                                        <input
                                            type="text"
                                            name="remitente_ci_nit"
                                            value={formData.remitente_ci_nit}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3564db]/30 focus:border-[#3564db] transition-all"
                                            placeholder="Ej: 1234567 o 123456789"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Descripción */}
                            <div className="space-y-6">
                                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-r from-[#3564db]/10 to-[#92b1ed]/10 rounded-lg flex items-center justify-center">
                                        <div className="w-3 h-3 bg-[#3564db] rounded-full"></div>
                                    </div>
                                    Descripción del Paquete / Documento
                                </h3>

                                <div className="space-y-3">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Detalle de la correspondencia *
                                    </label>
                                    <textarea
                                        name="descripcion"
                                        value={formData.descripcion || ''}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3564db]/30 focus:border-[#3564db] transition-all"
                                        rows={3}
                                        placeholder="Describe el contenido de la correspondencia, documentos, paquetes, etc."
                                        required
                                    />
                                </div>
                            </div>

                            {/* Respaldo con Fotografía */}
                            <div className="space-y-6">
                                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-r from-[#3564db]/10 to-[#92b1ed]/10 rounded-lg flex items-center justify-center">
                                        <Camera className="w-4 h-4 text-[#3564db]" />
                                    </div>
                                    Fotografía / Respaldo Visual
                                </h3>

                                <div>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="hidden"
                                        id="foto-correspondencia"
                                    />

                                    {!imagenPreview ? (
                                        <label
                                            htmlFor="foto-correspondencia"
                                            className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-[#3564db] hover:bg-blue-50/40 transition-all group"
                                        >
                                            <div className="w-12 h-12 bg-gray-100 group-hover:bg-blue-100 rounded-full flex items-center justify-center mb-3 transition-colors">
                                                <Upload className="w-6 h-6 text-gray-500 group-hover:text-[#3564db]" />
                                            </div>
                                            <span className="text-sm font-semibold text-gray-700 group-hover:text-[#3564db]">
                                                Haz clic para adjuntar o tomar foto del paquete
                                            </span>
                                            <span className="text-xs text-gray-500 mt-1">PNG, JPG, WEBP (Máx. 10MB)</span>
                                        </label>
                                    ) : (
                                        <div className="relative rounded-xl border border-gray-200 overflow-hidden bg-gray-50 p-2">
                                            <img
                                                src={imagenPreview}
                                                alt="Previsualización"
                                                className="w-full max-h-60 object-contain rounded-lg mx-auto"
                                            />
                                            <div className="mt-2 flex justify-between items-center px-2">
                                                <span className="text-xs text-gray-600 font-medium truncate max-w-xs">
                                                    {imagenFile?.name}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={handleRemoveImage}
                                                    className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                    Quitar foto
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Estado Inicial */}
                            <div className="space-y-6">
                                <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                                    <div className="w-8 h-8 bg-gradient-to-r from-[#3564db]/10 to-[#92b1ed]/10 rounded-lg flex items-center justify-center">
                                        <div className="w-3 h-3 bg-[#3564db] rounded-full"></div>
                                    </div>
                                    Estado Inicial
                                </h3>

                                <div className="space-y-3">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <label className={`flex flex-col items-center justify-center p-4 border rounded-lg cursor-pointer transition-all ${formData.estado === 'recibido' ? 'border-[#3564db] bg-[#3564db]/5' : 'border-gray-300 hover:border-gray-400'}`}>
                                            <input
                                                type="radio"
                                                name="estado"
                                                value="recibido"
                                                checked={formData.estado === 'recibido'}
                                                onChange={handleInputChange}
                                                className="sr-only"
                                            />
                                            <div className={`w-4 h-4 rounded-full border-2 ${formData.estado === 'recibido' ? 'border-[#3564db] bg-[#3564db]' : 'border-gray-400'}`}></div>
                                            <span className="mt-2 text-sm font-medium text-gray-700">Recibido</span>
                                            <span className="text-xs text-gray-500 mt-1 text-center">En recepción</span>
                                        </label>

                                        <label className={`flex flex-col items-center justify-center p-4 border rounded-lg cursor-pointer transition-all ${formData.estado === 'notificado' ? 'border-[#3564db] bg-[#3564db]/5' : 'border-gray-300 hover:border-gray-400'}`}>
                                            <input
                                                type="radio"
                                                name="estado"
                                                value="notificado"
                                                checked={formData.estado === 'notificado'}
                                                onChange={handleInputChange}
                                                className="sr-only"
                                            />
                                            <div className={`w-4 h-4 rounded-full border-2 ${formData.estado === 'notificado' ? 'border-[#3564db] bg-[#3564db]' : 'border-gray-400'}`}></div>
                                            <span className="mt-2 text-sm font-medium text-gray-700">Notificado</span>
                                            <span className="text-xs text-gray-500 mt-1 text-center">Cliente avisado</span>
                                        </label>

                                        <label className={`flex flex-col items-center justify-center p-4 border rounded-lg cursor-pointer transition-all ${formData.estado === 'entregado' ? 'border-[#3564db] bg-[#3564db]/5' : 'border-gray-300 hover:border-gray-400'}`}>
                                            <input
                                                type="radio"
                                                name="estado"
                                                value="entregado"
                                                checked={formData.estado === 'entregado'}
                                                onChange={handleInputChange}
                                                className="sr-only"
                                            />
                                            <div className={`w-4 h-4 rounded-full border-2 ${formData.estado === 'entregado' ? 'border-[#3564db] bg-[#3564db]' : 'border-gray-400'}`}></div>
                                            <span className="mt-2 text-sm font-medium text-gray-700">Entregado</span>
                                            <span className="text-xs text-gray-500 mt-1 text-center">Ya retirado</span>
                                        </label>

                                        <label className={`flex flex-col items-center justify-center p-4 border rounded-lg cursor-pointer transition-all ${formData.estado === 'cancelado' ? 'border-[#3564db] bg-[#3564db]/5' : 'border-gray-300 hover:border-gray-400'}`}>
                                            <input
                                                type="radio"
                                                name="estado"
                                                value="cancelado"
                                                checked={formData.estado === 'cancelado'}
                                                onChange={handleInputChange}
                                                className="sr-only"
                                            />
                                            <div className={`w-4 h-4 rounded-full border-2 ${formData.estado === 'cancelado' ? 'border-[#3564db] bg-[#3564db]' : 'border-gray-400'}`}></div>
                                            <span className="mt-2 text-sm font-medium text-gray-700">Cancelado</span>
                                            <span className="text-xs text-gray-500 mt-1 text-center">Cancelada</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-4 pt-8 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200"
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-[#3564db] to-[#92b1ed] hover:shadow-lg rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                                            Registrando Correspondencia...
                                        </>
                                    ) : (
                                        <>
                                            <div className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center">
                                                <div className="w-2 h-2 bg-white rounded-full"></div>
                                            </div>
                                            Registrar Correspondencia
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        </>
    );
};

export default IniciarCorrespondenciaModal;

